// =============================================================================
// useFangateCache.js — localStorage-backed TTL + SWR cache for Fangate calls
// =============================================================================
// Centralises Fangate caching so per-account product / wallet / collection /
// consent fetches don't roundtrip on every navigation. Two patterns:
//
//   1. wrap(key, ttl, fetcher)
//      Cache-first. Returns the cached value if fresh; otherwise fetches +
//      writes through. Use for data where a small staleness window is fine
//      (products, collection, folders).
//
//   2. wrapSWR(key, ttl, fetcher, onFresh)
//      Stale-while-revalidate. Returns the cached value SYNCHRONOUSLY (if
//      any), kicks off a background fetch, and calls onFresh when the
//      response lands. Use for "live-ish" panels where instant render
//      matters more than absolute freshness (wallet balance).
//
// Keys are namespaced under `fg_cache:v1:` so bumping the namespace (when
// we change a stored shape) is a no-deploy migration: rev the v1 → v2.
//
// All storage operations are wrapped in try/catch — Safari private mode
// and quota-exceeded both throw on localStorage writes. We degrade to
// "no cache" silently instead of breaking the UI.
// =============================================================================

const NAMESPACE = 'fg_cache:v1:';

const now = () => Date.now();
const k = key => NAMESPACE + key;

// ---- Low-level primitives --------------------------------------------------

/** Read raw cache entry. Returns { data, ts } or null. */
export function read(key) {
  try {
    const raw = localStorage.getItem(k(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.ts !== 'number') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/** Write a value with a current timestamp. Best-effort — quota errors are swallowed. */
export function write(key, data) {
  try {
    localStorage.setItem(k(key), JSON.stringify({ data, ts: now() }));
  } catch (_) {
    // Quota exceeded or Safari private mode → try a light prune of
    // anything older than 1 hour and retry once. If still failing, give up
    // gracefully — caching is an optimisation, not a requirement.
    try {
      pruneOlderThan(60 * 60 * 1000);
      localStorage.setItem(k(key), JSON.stringify({ data, ts: now() }));
    } catch (_) { /* no-op */ }
  }
}

/** Drop a single cache entry. */
export function invalidate(key) {
  try { localStorage.removeItem(k(key)); } catch (_) { /* no-op */ }
}

/** Drop every cache entry whose key starts with the given prefix.
 *  Used to flush "all products for account X" in one call. */
export function invalidatePrefix(prefix) {
  try {
    const full = NAMESPACE + prefix;
    // Snapshot keys first — removeItem during iteration is undefined behaviour.
    const victims = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(full)) victims.push(key);
    }
    victims.forEach(key => localStorage.removeItem(key));
  } catch (_) { /* no-op */ }
}

/** Prune any cache entry older than `maxAgeMs`. */
export function pruneOlderThan(maxAgeMs) {
  try {
    const cutoff = now() - maxAgeMs;
    const victims = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(NAMESPACE)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.ts && parsed.ts < cutoff) victims.push(key);
      } catch (_) { victims.push(key); }  // corrupt → drop
    }
    victims.forEach(key => localStorage.removeItem(key));
  } catch (_) { /* no-op */ }
}

// ---- Inspection ------------------------------------------------------------

/** Returns `{ data, age, isFresh }` for a key, or null if no entry. */
export function check(key, ttlMs) {
  const entry = read(key);
  if (!entry) return null;
  const age = now() - entry.ts;
  return { data: entry.data, age, isFresh: age < ttlMs };
}

// ---- High-level patterns ---------------------------------------------------

/**
 * Cache-first. Returns the cached value if fresh, otherwise invokes
 * `fetcher()` and writes through. Forces a refetch when `force: true`.
 *
 *   const products = await wrap(
 *     `products:${accountId}`, 5 * 60_000,
 *     () => FangateAPI.listProducts({ limit: 50 })
 *   );
 */
export async function wrap(key, ttlMs, fetcher, { force = false } = {}) {
  if (!force) {
    const hit = check(key, ttlMs);
    if (hit?.isFresh) return hit.data;
  }
  const data = await fetcher();
  write(key, data);
  return data;
}

/**
 * Stale-while-revalidate. Returns the cached value SYNCHRONOUSLY (even if
 * stale), fires a background fetch, calls `onFresh(data, meta)` when new
 * data lands.
 *
 *   const { cached, isStale, promise } = wrapSWR(
 *     `wallet:${accountId}`, 60_000,
 *     () => FangateAPI.getWallet(),
 *     newData => { wallet.value = newData; }
 *   );
 *   if (cached) wallet.value = cached;
 *
 * The returned `promise` resolves once the background fetch is done — use
 * it if you need to await the revalidation (e.g. in an integration test).
 */
export function wrapSWR(key, ttlMs, fetcher, onFresh) {
  const hit = check(key, ttlMs);
  const promise = (async () => {
    try {
      const data = await fetcher();
      write(key, data);
      onFresh?.(data, { wasStale: !!hit && !hit.isFresh, cached: hit?.data ?? null });
    } catch (error) {
      onFresh?.(null, { error, wasStale: !!hit && !hit.isFresh, cached: hit?.data ?? null });
    }
  })();
  return {
    cached:   hit?.data ?? null,
    age:      hit?.age ?? null,
    isStale:  hit ? !hit.isFresh : true,
    promise,
  };
}
